import React, { useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import BulletinsPresence from './BulletinsPresence';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { Clock, Diamond, MessageSquare } from 'lucide-react';

interface BulletinsTabsProps {
  activeTab: string;
  onTabChange: (value: string) => void;
  activeCategory?: string;
  showTabs?: boolean;
}

const BulletinsTabs = ({
  activeTab,
  onTabChange,
  activeCategory = 'chrono',
  showTabs = false
}: BulletinsTabsProps) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Query to get unread counts with optimized caching strategy
  const { data: unreadCounts } = useQuery({
    queryKey: ['unreadBulletins'],
    queryFn: async () => {
      const lastVisitTime = localStorage.getItem('lastBulletinsVisit') || new Date(0).toISOString();
      try {
        const { data, error } = await supabase.from('bulletins').select('is_update', {
          count: 'exact'
        }).gt('created_at', lastVisitTime).in('is_update', [true, false]);
        if (error) throw error;
        const regularCount = data?.filter(b => !b.is_update).length || 0;
        const updatesCount = data?.filter(b => b.is_update).length || 0;
        return {
          regular: regularCount,
          updates: updatesCount
        };
      } catch (error) {
        console.error('Error fetching unread counts:', error);
        return {
          regular: 0,
          updates: 0
        };
      }
    },
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: !isMobile
  });

  const handleTabChange = useCallback((value: string) => {
    localStorage.setItem('lastBulletinsVisit', new Date().toISOString());
    onTabChange(value);
  }, [onTabChange]);
  
  const formatCount = (count: number) => {
    return count > 99 ? '99+' : count.toString();
  };
  
  return (
    <div className="flex flex-col gap-1 mb-2 sm:mb-0.5 min-h-[36px] py-0">
      <div className="px-2 self-end">
        <BulletinsPresence />
      </div>
      {showTabs && (
        <div className="flex flex-col gap-2">
          <Tabs defaultValue="pour-toi" value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="bg-[#1A1F2C]/50 backdrop-blur-sm">
              <TabsTrigger value="pour-toi" className="data-[state=active]:bg-[#9b87f5]/20 relative text-[#9b87f5]">
                Pour Toi
                {unreadCounts?.regular ? (
                  <Badge variant="secondary" className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center text-xs bg-[#9b87f5]/20 text-[#9b87f5]">
                    {formatCount(unreadCounts.regular)}
                  </Badge>
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="top-follows" className="data-[state=active]:bg-secondary/20 text-secondary relative">
                Follow Feed
              </TabsTrigger>
              <TabsTrigger value="updates" className="data-[state=active]:bg-accent/20 text-accent relative">
                Updates
                {unreadCounts?.updates ? (
                  <Badge variant="secondary" className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center text-xs bg-accent/20 text-accent">
                    {formatCount(unreadCounts.updates)}
                  </Badge>
                ) : null}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {activeTab === 'pour-toi' && (
            <div className="flex gap-2 px-1">
              <button
                onClick={() => navigate(`/bulletins/${activeTab}/chrono`)}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeCategory === 'chrono'
                    ? 'bg-[#9b87f5]/20 text-[#9b87f5]'
                    : 'text-muted-foreground hover:text-[#9b87f5] hover:bg-[#9b87f5]/10'
                }`}
              >
                <Clock 
                  size={16} 
                  className={`${
                    activeCategory === 'chrono' 
                      ? 'animate-[spin_1s_ease-in-out_1]' 
                      : ''
                  }`}
                />
                Chrono
              </button>
              <button
                onClick={() => navigate(`/bulletins/${activeTab}/all`)}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-[#9b87f5]/20 text-[#9b87f5]'
                    : 'text-muted-foreground hover:text-[#9b87f5] hover:bg-[#9b87f5]/10'
                }`}
              >
                <Diamond size={16} />
                All Posts
              </button>
              <button
                onClick={() => navigate('/loops')}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-[#64748b] hover:bg-[#64748b]/10"
              >
                <MessageSquare size={16} />
                Loops
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulletinsTabs;